import { updateSiteConfig } from '#imports';

import { HREFLANG_ALTERNATE_ROUTES, SCHOOLS_HREFLANG_ALTERNATE_ROUTES } from '~/constants/routes/unified';
import { ClusterProduct } from '~/graphql/generated/graphql';
import { useClusterRegistryStore } from '~/stores/clusterRegistry';
import { useConfigStore } from '~/stores/config';
import { buildHreflangLinks } from '~/utils/clusters';

// Year13 operates a separate legal entity per region, so the brand Organization a
// page declares is the one for the current cluster's country. Unknown clusters omit
// legalName rather than guess.
const LEGAL_NAME_BY_COUNTRY: Record<string, string> = {
  AUS: 'Year13 Pty Ltd',
  USA: 'Year13 Inc',
};

export default defineNuxtPlugin({
  enforce: 'pre',
  name: 'seo',
  dependsOn: ['load-config'],
  setup() {
    const nuxtApp = useNuxtApp();
    const { baseUrl, code, origin } = useCurrentCluster();
    const { clusters } = useGlobalClusters();
    const registry = useClusterRegistryStore();
    const countryCode = code.value ? registry.findByCode(code.value)?.country?.code : undefined;
    // og:locale + <html lang> use the cluster's DB-backed defaultLocale (the same
    // source 02.loadConfig uses for the active i18n locale) — no hardcoded map, so a
    // new cluster's locale flows through automatically.
    const locale = useConfigStore().publicConfig?.country?.defaultLocale;

    // Per-cluster og:locale + a default Twitter card, set globally. Per-page
    // og:title/description/image come from the pages (useSeoMeta); Twitter falls back
    // to those og: tags when no twitter:* equivalent is present. (<html lang> is
    // handled by siteConfig.currentLocale in updateSiteConfig above.)
    useSeoMeta({
      ogLocale: locale ? locale.replace('-', '_') : undefined,
      twitterCard: 'summary_large_image',
    });

    if (import.meta.server) {
      updateSiteConfig({
        url: baseUrl.value,
        name: nuxtApp.$i18n.t('general.anyway.name'),
        description: nuxtApp.$i18n.t('general.anyway.description'),
        // Drives <html lang> (nuxt-seo-utils reads siteConfig.currentLocale). Without
        // this it stays at the default en-US even on the AU cluster.
        currentLocale: locale,
      });

      // The brand Organization identity (name, logo, sameAs) is configured statically
      // in nuxt.config (schemaOrg.identity); url comes from the per-cluster site
      // config. Only legalName is region-specific (separate AU/US legal entities),
      // so merge it onto the identity node by its absolute @id.
      const legalName = countryCode ? LEGAL_NAME_BY_COUNTRY[countryCode] : undefined;
      if (legalName) {
        useSchemaOrg([
          { '@type': 'Organization', '@id': `${baseUrl.value}/#identity`, legalName },
        ]);
      }

      const route = useRoute();
      // The schools product mirrors only its landing page across clusters, so its
      // hreflang surface is that pair alone. The demo request is indexable but
      // declares no alternates. Consumer clusters keep the full allowlist.
      const isSchoolsCluster = code.value
        ? registry.findByCode(code.value)?.product === ClusterProduct.Schools
        : false;
      const hreflangRoutes = isSchoolsCluster ? SCHOOLS_HREFLANG_ALTERNATE_ROUTES : HREFLANG_ALTERNATE_ROUTES;
      useHead({
        link: computed(() => {
          // Caddy strips the cluster prefix before Nuxt, so route.path is already the
          // shared, prefix-free path that every cluster serves this route at.
          const localPath = route.path;

          // Opt-in: only structural routes present in every cluster declare cross-cluster
          // alternates. Independent per-region content (its own slugs) is omitted so we
          // never assert a counterpart that 404s.
          if (!hreflangRoutes.includes(localPath)) {
            return [];
          }

          return buildHreflangLinks(clusters.value, code.value, origin.value, localPath);
        }),
      });
    }
  },
});
